import React, { useState, useEffect } from 'react';
import { Trash, X, Sparkle, CookingPot, Clock, Warning } from '@phosphor-icons/react';
import { Modal, InputField, EmptyState } from './Shared';
import { apiOrigin } from '../../utils/apiBase';
import DishDetailDrawer from './DishDetailDrawer';

// Convert a relative /api/... URL returned by the backend into an absolute one.
const absUrl = (u) => (u && u.startsWith('/') ? `${apiOrigin()}${u}` : u);

function RecipeImage({ api, name, recipeId, cachedSrc, onSaved }) {
  const [src, setSrc] = useState(cachedSrc || '');
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (cachedSrc) setSrc(cachedSrc); }, [cachedSrc]);

  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.post('/meals/dish-images', { dish_name: name });
      if (r.data?.image_url) setSrc(absUrl(r.data.image_url));
      if (onSaved) onSaved();
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Image generation failed');
    }
    setLoading(false);
  };

  const regenerate = async () => {
    if (!window.confirm('Replace the existing image with a freshly generated one?')) return;
    setLoading(true);
    try {
      const r = await api.post('/meals/dish-images', { dish_name: name, force: true });
      if (r.data?.image_url) setSrc(absUrl(r.data.image_url));
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Image generation failed');
    }
    setLoading(false);
  };

  if (src) {
    return (
      <div className="relative h-32 rounded-xl overflow-hidden mb-3 group" data-testid={`recipe-image-${recipeId}`}>
        <img src={src} alt={name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
        <button onClick={(e) => { e.stopPropagation(); regenerate(); }} disabled={loading}
          data-testid={`recipe-image-regen-${recipeId}`}
          className="absolute top-1.5 right-1.5 px-2 py-1 rounded-lg bg-black/60 text-white text-[9px] font-bold opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm disabled:opacity-50">
          {loading ? '…' : '↻'}
        </button>
      </div>
    );
  }

  return (
    <div className="h-32 rounded-xl mb-3 flex items-center justify-center"
      style={{ background: 'rgba(255,255,255,0.04)', border: '1px dashed rgba(255,255,255,0.10)' }}
      data-testid={`recipe-image-placeholder-${recipeId}`}>
      <button onClick={(e) => { e.stopPropagation(); generate(); }} disabled={loading}
        data-testid={`recipe-image-gen-${recipeId}`}
        className="text-[10px] font-bold text-purple-300 hover:text-purple-200 flex items-center gap-1.5 disabled:opacity-50">
        <Sparkle size={12} weight="fill" /> {loading ? 'Painting…' : 'Generate cover image'}
      </button>
    </div>
  );
}

function AIRecipeModal({ api, onClose, onSaved }) {
  const [form, setForm] = useState({
    dish_name: '',
    cuisine: '',
    category: 'main_course',
    servings: 4,
    hints: '',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [imgSrc, setImgSrc] = useState('');
  const [imgLoading, setImgLoading] = useState(false);

  const generate = async () => {
    setLoading(true); setError(''); setResult(null); setImgSrc('');
    try {
      const res = await api.post('/meals/recipes/ai-generate', { ...form, save: true });
      setResult(res.data);
      onSaved();
      const dish = res.data?.name || form.dish_name;
      if (dish) {
        setImgLoading(true);
        api.post('/meals/dish-images', { dish_name: dish })
          .then(r => { if (r.data?.image_url) setImgSrc(absUrl(r.data.image_url)); })
          .catch(() => { /* swallow */ })
          .finally(() => setImgLoading(false));
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Generation failed.');
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div className="bg-neutral-900 border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[92vh] overflow-y-auto"
        onClick={e => e.stopPropagation()} data-testid="ai-recipe-modal">
        <div className="sticky top-0 z-10 px-5 py-3 border-b border-white/10 bg-neutral-900 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkle size={18} weight="fill" className="text-purple-300" />
            <h3 className="text-sm font-bold text-white">AI Generate Recipe</h3>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white" data-testid="ai-recipe-close"><X size={18} /></button>
        </div>

        {!result && (
          <div className="p-5 space-y-3">
            {error && <div className="text-xs text-red-300 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30">{error}</div>}
            <div className="rounded-lg p-2 text-[11px] text-purple-200" style={{ background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.20)' }}>
              Leave dish name empty and the chef will surprise you with a new dish that fits your food profile.
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Dish name (optional)</label>
              <input value={form.dish_name} onChange={e => setForm(f => ({ ...f, dish_name: e.target.value }))}
                placeholder="e.g., Masala Khichdi, Methi Thepla…" data-testid="ai-recipe-name"
                className="w-full px-3 py-2 mt-1 rounded-lg bg-white/5 border border-white/10 text-sm outline-none" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Cuisine</label>
                <input value={form.cuisine} onChange={e => setForm(f => ({ ...f, cuisine: e.target.value }))}
                  placeholder="Indian, Gujarati…" data-testid="ai-recipe-cuisine"
                  className="w-full px-3 py-2 mt-1 rounded-lg bg-white/5 border border-white/10 text-sm outline-none" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Category</label>
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  data-testid="ai-recipe-category"
                  className="w-full px-3 py-2 mt-1 rounded-lg bg-white/5 border border-white/10 text-sm outline-none">
                  {['breakfast', 'lunch', 'dinner', 'snack', 'main_course', 'dessert', 'beverage'].map(c =>
                    <option key={c} value={c} style={{ background: '#0a0a0a' }}>{c.replace('_', ' ')}</option>
                  )}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Servings</label>
                <input type="number" min="1" max="50" value={form.servings}
                  onChange={e => setForm(f => ({ ...f, servings: e.target.value }))}
                  data-testid="ai-recipe-servings"
                  className="w-full px-3 py-2 mt-1 rounded-lg bg-white/5 border border-white/10 text-sm outline-none" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Hints (optional)</label>
              <textarea rows="2" value={form.hints} onChange={e => setForm(f => ({ ...f, hints: e.target.value }))}
                placeholder="e.g., quick weeknight, kid-friendly, festive…" data-testid="ai-recipe-hints"
                className="w-full px-3 py-2 mt-1 rounded-lg bg-white/5 border border-white/10 text-sm outline-none" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose} disabled={loading} className="px-3 py-2 rounded-lg bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10">Cancel</button>
              <button onClick={generate} disabled={loading} data-testid="ai-recipe-generate"
                className="px-4 py-2 rounded-lg bg-purple-500 text-white text-xs font-bold disabled:opacity-50">
                {loading ? 'Cooking up the recipe…' : '✨ Generate'}
              </button>
            </div>
          </div>
        )}

        {result && (
          <div className="p-5 space-y-3" data-testid="ai-recipe-result">
            {imgSrc ? (
              <div className="rounded-xl overflow-hidden" data-testid="ai-recipe-image">
                <img src={imgSrc} alt={result.name} loading="lazy" decoding="async" className="w-full h-44 object-cover" />
              </div>
            ) : imgLoading ? (
              <div className="rounded-xl h-44 flex items-center justify-center text-[11px] text-purple-300"
                style={{ background: 'rgba(168,85,247,0.06)', border: '1px dashed rgba(168,85,247,0.30)' }}>
                <span className="flex items-center gap-2">
                  <Sparkle size={14} weight="fill" className="animate-pulse" /> Painting cover image…
                </span>
              </div>
            ) : null}
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <h4 className="text-base font-bold text-white">{result.name}</h4>
              <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-500/30">AI generated</span>
            </div>
            {result.description && <p className="text-xs text-neutral-300">{result.description}</p>}
            <div className="flex flex-wrap gap-2 text-[10px] text-neutral-500">
              <span className="px-2 py-0.5 rounded-full bg-white/5">{result.cuisine}</span>
              <span className="px-2 py-0.5 rounded-full bg-white/5 capitalize">{(result.category || '').replace('_',' ')}</span>
              <span>{result.servings} servings</span>
              {(result.prep_time_mins + result.cook_time_mins) > 0 && (
                <span>{result.prep_time_mins + result.cook_time_mins} min total</span>
              )}
            </div>
            {result.ingredients?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-1.5">Ingredients</p>
                <ul className="space-y-0.5">
                  {result.ingredients.map((i, idx) => (
                    <li key={idx} className="text-[12px] text-neutral-300">
                      <span className="text-purple-300 font-bold">{i.quantity} {i.unit}</span> {i.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {result.instructions?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-1.5">Instructions</p>
                <ol className="space-y-1.5 list-decimal list-inside">
                  {result.instructions.map((s, idx) => (
                    <li key={idx} className="text-[12px] text-neutral-300">{s}</li>
                  ))}
                </ol>
              </div>
            )}
            <div className="flex gap-2 justify-end pt-2 border-t border-white/10">
              <button onClick={() => { setResult(null); setError(''); }} data-testid="ai-recipe-again"
                className="px-3 py-2 rounded-lg bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10">
                Generate another
              </button>
              <button onClick={onClose} data-testid="ai-recipe-done"
                className="px-4 py-2 rounded-lg bg-purple-500 text-white text-xs font-bold hover:opacity-90">
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function RecipesTab({ api, recipes, onUpdate }) {
  const [showForm, setShowForm] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [detailDish, setDetailDish] = useState(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [imageMap, setImageMap] = useState({});
  const [form, setForm] = useState({ name: '', description: '', category: 'main_course', prep_time_mins: 0, cook_time_mins: 0, servings: 4, ingredients: '', instructions: '', tags: '' });

  const recipeKey = (n) => (n || '').toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim().replace(/ /g, '_').slice(0, 60);

  useEffect(() => {
    if (recipes.length === 0) { setImageMap({}); return; }
    let cancelled = false;
    const names = recipes.map(r => r.name).filter(Boolean);
    if (names.length === 0) return;
    api.post('/meals/dish-images/lookup', { names })
      .then(r => {
        if (cancelled) return;
        const m = {};
        Object.entries(r.data || {}).forEach(([k, v]) => {
          if (v.image_url) m[k] = absUrl(v.image_url);
        });
        setImageMap(m);
      })
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, [api, recipes]);

  const hasTestData = recipes.some(r => /^TEST_/i.test(r.name || ''));

  const handleSave = async (e) => {
    e.preventDefault();
    const ingredients = form.ingredients.split('\n').filter(Boolean).map(line => {
      const parts = line.split(',').map(s => s.trim());
      return { name: parts[0] || '', quantity: parts[1] || '', unit: parts[2] || '' };
    });
    const instructions = form.instructions.split('\n').filter(Boolean);
    const tags = form.tags.split(',').map(s => s.trim()).filter(Boolean);
    await api.post('/meals/recipes', { ...form, ingredients, instructions, tags, diet_type: 'Jain Vegetarian' });
    setShowForm(false);
    setForm({ name: '', description: '', category: 'main_course', prep_time_mins: 0, cook_time_mins: 0, servings: 4, ingredients: '', instructions: '', tags: '' });
    onUpdate();
  };

  const runCleanup = async () => {
    if (!window.confirm('This will delete every TEST_* recipe, rating, dish-pref and strip TEST_* from meal plans. Continue?')) return;
    setCleanupBusy(true);
    try {
      const dryRun = await api.post('/meals/admin/cleanup-test-data', { dry_run: true });
      const d = dryRun.data || {};
      const total = (d.deleted?.dish_ratings || 0) + (d.deleted?.recipes || 0) + (d.updated?.meal_plans || 0) + (d.updated?.food_preferences || 0);
      if (total === 0) {
        window.alert('No TEST_* data found — already clean.');
      } else {
        if (!window.confirm(`Found:\n• ${d.deleted?.dish_ratings || 0} ratings\n• ${d.deleted?.recipes || 0} recipes\n• ${d.updated?.meal_plans || 0} meal-plan days\n• ${d.updated?.food_preferences || 0} food-preference docs\n\nDelete now?`)) {
          setCleanupBusy(false);
          return;
        }
        await api.post('/meals/admin/cleanup-test-data', { dry_run: false });
        window.alert('Cleanup complete.');
      }
      onUpdate();
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Cleanup failed.');
    }
    setCleanupBusy(false);
  };

  return (
    <div className="space-y-4">
      {hasTestData && (
        <div className="rounded-xl p-3 flex items-center justify-between gap-3 flex-wrap"
          style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}>
          <div className="flex items-center gap-2 min-w-0">
            <Warning size={16} weight="fill" className="text-amber-400" />
            <p className="text-[12px] text-amber-200">Leftover TEST_* data detected. Run cleanup to remove it.</p>
          </div>
          <button onClick={runCleanup} disabled={cleanupBusy} data-testid="recipes-cleanup-btn"
            className="px-3 py-1.5 rounded-lg bg-amber-500/20 text-amber-200 border border-amber-500/40 text-[11px] font-bold hover:bg-amber-500/30 disabled:opacity-50">
            {cleanupBusy ? 'Working…' : 'Clean up test data'}
          </button>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-neutral-400">{recipes.length} recipe(s)</p>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowAI(true)} data-testid="ai-recipe-btn"
            className="px-3 sm:px-4 py-2 rounded-2xl bg-purple-500/15 text-purple-300 border border-purple-500/40 text-xs sm:text-sm font-bold flex items-center gap-1.5 hover:bg-purple-500/25">
            <Sparkle size={14} weight="fill" /> AI Generate
          </button>
          <button onClick={() => setShowForm(true)} data-testid="add-recipe-btn"
            className="px-3 sm:px-4 py-2 rounded-2xl bg-[#FF6B35] text-white text-xs sm:text-sm font-semibold flex items-center gap-1.5 btn-glow flex-shrink-0">
            <CookingPot size={14} /> Add Recipe
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {recipes.map(r => (
          <div key={r.recipe_id} data-testid={`recipe-${r.recipe_id}`}
            onClick={() => setDetailDish(r.name)}
            className="rounded-2xl p-4 cursor-pointer transition hover:bg-white/[0.06]"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <RecipeImage api={api} name={r.name} recipeId={r.recipe_id} cachedSrc={imageMap[recipeKey(r.name)]} />
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3 className="text-sm font-semibold">{r.name}</h3>
              {r.ai_generated && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-500/30 shrink-0">AI</span>}
            </div>
            {r.description && <p className="text-xs text-neutral-400 mb-2">{r.description}</p>}
            <div className="flex items-center gap-2 text-[10px] text-neutral-500 flex-wrap">
              <span className="px-2 py-0.5 rounded-full bg-white/5 capitalize">{r.category?.replace('_', ' ')}</span>
              {(r.prep_time_mins > 0 || r.cook_time_mins > 0) && (
                <span className="flex items-center gap-1"><Clock size={10} />{r.prep_time_mins + r.cook_time_mins}min</span>
              )}
              <span>{r.servings} servings</span>
            </div>
            {r.ingredients?.length > 0 && (
              <p className="text-[11px] text-neutral-500 mt-2">{r.ingredients.map(i => i.name).join(', ')}</p>
            )}
            <button onClick={async (e) => { e.stopPropagation(); await api.delete(`/meals/recipes/${r.recipe_id}`); onUpdate(); }}
              className="mt-2 text-[11px] text-red-400 hover:underline flex items-center gap-1"><Trash size={10} /> Delete</button>
          </div>
        ))}
        {recipes.length === 0 && (
          <EmptyState text="No recipes added yet. Click 'AI Generate' to let the chef create one, or add manually." />
        )}
      </div>

      {showAI && (
        <AIRecipeModal api={api} onClose={() => setShowAI(false)} onSaved={onUpdate} />
      )}

      {showForm && (
        <Modal onClose={() => setShowForm(false)}>
          <h3 className="text-lg font-bold mb-4">Add Recipe</h3>
          <form onSubmit={handleSave} className="space-y-3">
            <InputField label="Name" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} testId="recipe-name" required />
            <InputField label="Description" value={form.description} onChange={v => setForm(f => ({ ...f, description: v }))} testId="recipe-desc" />
            <div>
              <label className="text-xs font-semibold text-neutral-500 uppercase block mb-1">Category</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none">
                {['breakfast', 'lunch', 'dinner', 'snack', 'main_course', 'dessert', 'side_dish'].map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <InputField label="Prep (min)" type="number" value={form.prep_time_mins} onChange={v => setForm(f => ({ ...f, prep_time_mins: Number(v) }))} />
              <InputField label="Cook (min)" type="number" value={form.cook_time_mins} onChange={v => setForm(f => ({ ...f, cook_time_mins: Number(v) }))} />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-500 uppercase block mb-1">Ingredients (one per line: name, qty, unit)</label>
              <textarea value={form.ingredients} onChange={e => setForm(f => ({ ...f, ingredients: e.target.value }))} rows={3}
                placeholder="Paneer, 250, grams" className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none resize-none" />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-500 uppercase block mb-1">Instructions (one step per line)</label>
              <textarea value={form.instructions} onChange={e => setForm(f => ({ ...f, instructions: e.target.value }))} rows={3}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none resize-none" />
            </div>
            <InputField label="Tags (comma separated)" value={form.tags} onChange={v => setForm(f => ({ ...f, tags: v }))} />
            <button type="submit" data-testid="save-recipe-btn" className="w-full py-3 rounded-xl bg-[#FF6B35] text-white text-sm font-bold">Save Recipe</button>
          </form>
        </Modal>
      )}

      {detailDish && (
        <DishDetailDrawer api={api} dishName={detailDish} onClose={() => setDetailDish(null)} />
      )}
    </div>
  );
}
